import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  ClipboardList,
  Wrench,
  Package,
  Users,
  Calendar,
  Clock,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  HardHat,
  Truck,
  ShoppingCart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { canOpen, navLabel } from '../lib/access';
import { UnreadProvider, useUnread, unreadTotal } from '../context/UnreadContext';
import { UnreadBadge } from '../components/dashboard/UnreadBadge';
import { NotificationBell } from '../components/dashboard/NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';


const LOGO_URL = "/assets/logo-le-bleu-des-champs.png";


const navItems = [
  { name: 'Tableau de bord', path: '/dashboard', icon: LayoutDashboard },
  { name: 'Appels de service', ownName: 'Mes appels', ownPermission: 'service_requests.view', path: '/dashboard/service-requests', icon: ClipboardList, unread: ['service_requests'] },
  { name: 'Soumissions', path: '/dashboard/quotes', icon: FileText, unread: ['quotes', 'cleaning_requests', 'part_orders'] },
  { name: 'Chantiers', ownName: 'Mes chantiers', ownPermission: 'projects.view', path: '/dashboard/projects', icon: HardHat },
  { name: 'Inventaire', path: '/dashboard/inventory', icon: Package },
  { name: 'Matériel à facturer', path: '/dashboard/billable-materials', icon: FileText },
  { name: 'Bons de commande', path: '/dashboard/purchase-orders', icon: ShoppingCart },
  { name: 'Matériel', path: '/dashboard/equipment', icon: Truck },
  { name: 'Employés', path: '/dashboard/employees', icon: Users },
  { name: 'Horodatage', path: '/dashboard/time-entries', icon: Clock },
  { name: 'Congés', ownName: 'Mes congés', path: '/dashboard/leave-requests', icon: Calendar, unread: ['leave_requests'] },
  { name: 'Mon profil', path: '/dashboard/profile', icon: User, hideForAdmin: true },
  { name: 'Paramètres', path: '/dashboard/settings', icon: Settings, adminOnly: true },
];

export const DashboardLayout = ({ children }) => (
  <UnreadProvider>
    <DashboardLayoutContent>{children}</DashboardLayoutContent>
  </UnreadProvider>
);

const DashboardLayoutContent = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isAdmin, can, scopeOf } = useAuth();
  const { counts } = useUnread();
  const location = useLocation();
  const navigate = useNavigate();

  const visibleNavItems = navItems
    .filter((item) => canOpen(can, item.path) && !(item.hideForAdmin && isAdmin) && (!item.adminOnly || isAdmin))
    .map((item) => ({ ...item, name: navLabel(item, scopeOf, can) }));
  const totalUnread = unreadTotal(counts, visibleNavItems.flatMap(item => item.unread || []));

  const handleLogout = () => {
    logout();
    navigate('/connexion');
  };


  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-50 glass shadow-sm">
        <div className="flex items-center justify-between h-16 px-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-slate-100 relative"
            data-testid="sidebar-toggle"
            aria-label={totalUnread ? `Menu, ${totalUnread} non lu${totalUnread > 1 ? 's' : ''}` : 'Menu'}
          >
            <Menu className="w-6 h-6 text-[#1a3a5c]" />
            {totalUnread > 0 && (
              <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
            )}
          </button>
          <img src={LOGO_URL} alt="Le Bleu des Champs" className="h-10" />
          <div className="flex items-center gap-1">
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full bg-[#1a3a5c] text-white">
                <User className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/dashboard/profile')}>
                Mon profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>


      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}


      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-72 bg-[#1a3a5c] transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar header */}
          <div className="flex items-center justify-between h-20 px-6 border-b border-white/10">
            <img src={LOGO_URL} alt="Le Bleu des Champs" className="h-12 brightness-0 invert" />
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/10 text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>


          {/* User info */}
          <div className="px-6 py-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#00b4d8] flex items-center justify-center text-white font-semibold">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div>
                <p className="text-white font-medium">{user?.first_name} {user?.last_name}</p>
                <p className="text-slate-400 text-sm">{user?.role_name || (user?.role === 'admin' ? 'Administrateur' : user?.role)}</p>
              </div>
            </div>
          </div>


          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    isActive
                      ? 'bg-[#00b4d8] text-white'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`nav-${item.path.split('/').pop()}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                  <UnreadBadge count={unreadTotal(counts, item.unread || [])} className="ml-auto" />
                </Link>
              );
            })}
          </nav>


          {/* Bottom actions */}
          <div className="p-4 border-t border-white/10">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-white/10 hover:text-white transition-colors mb-2"
            >
              <Settings className="w-5 h-5" />
              <span>Voir le site</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors w-full"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>


      {/* Main content */}
      <div className="lg:pl-72">
        {/* Desktop header */}
        <header className="hidden lg:flex sticky top-0 z-40 glass h-16 items-center justify-between px-8 shadow-sm">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-[#1a3a5c]">
              {visibleNavItems.find(item => item.path === location.pathname)?.name || 'Dashboard'}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-slate-100" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-[#1a3a5c] flex items-center justify-center text-white text-sm font-semibold">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </div>
                  <span className="text-sm font-medium text-slate-700">{user?.first_name}</span>
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/dashboard/profile')}>
                  <User className="w-4 h-4 mr-2" />
                  Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>


        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};


export default DashboardLayout;
